import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { apiService } from '../services/apiService';

const Analytics = ({ showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countries, setCountries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, campaignsRes, trackingRes] = await Promise.all([
        apiService.getCountries(),
        apiService.getCampaigns(),
        apiService.getTrackingData()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (campaignsRes.success) setCampaigns(campaignsRes.campaigns);
      if (trackingRes.success) setTrackingData(trackingRes.tracking_data);
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos de analytics');
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    let filtered = trackingData;

    if (selectedCountry !== 'all') {
      filtered = filtered.filter(item => item.country_code === selectedCountry);
    }

    if (selectedCampaign !== 'all') {
      filtered = filtered.filter(item => item.campaign_id === selectedCampaign);
    }

    return filtered;
  };

  // Calcular canales implementados (canales únicos con tracking_data activo)
  const calculateImplementedChannels = () => {
    const filtered = getFilteredData();
    const activeChannels = new Set();
    filtered.forEach(item => {
      if (item.implementation_percentage >= 50) {
        activeChannels.add(item.channel_id);
      }
    });
    return activeChannels.size;
  };

  // Calcular países implementados (países con ≥50% canales con implementation_percentage >= 50)
  const calculateImplementedCountries = () => {
    const filtered = getFilteredData();

    // Si hay campaña seleccionada, calcular por campaña
    if (selectedCampaign !== 'all') {
      const countryStats = {};

      // Agrupar por país
      filtered.forEach(item => {
        if (!countryStats[item.country_code]) {
          countryStats[item.country_code] = {
            total: 0,
            active: 0,
            country_name: item.country_name
          };
        }
        countryStats[item.country_code].total++;
        if (item.implementation_percentage >= 50) {
          countryStats[item.country_code].active++;
        }
      });
      
      // Contar países con ≥50% implementación
      let implemented = 0;
      Object.values(countryStats).forEach(stats => {
        const percentage = stats.total > 0 ? (stats.active / stats.total) * 100 : 0;
        if (percentage >= 50) {
          implemented++;
        }
      });
      
      return {
        implemented,
        total: Object.keys(countryStats).length,
        countryStats
      };
    }
    
    // Si solo hay país seleccionado, contar países únicos con al menos un canal activo
    if (selectedCountry !== 'all') {
      const hasActiveChannel = filtered.some(item => item.implementation_percentage >= 50);
      return {
        implemented: hasActiveChannel ? 1 : 0,
        total: 1,
        countryStats: {}
      };
    }
    
    return { implemented: 0, total: 0, countryStats: {} };
  };

  // Preparar datos para gráfico pie de países
  const prepareCountriesPieData = () => {
    const countryData = calculateImplementedCountries();
    return [
      { name: 'Implementados', value: countryData.implemented },
      { name: 'No Implementados', value: countryData.total - countryData.implemented }
    ];
  };

  // Preparar datos para gráfico pie de canales
  const prepareChannelsPieData = () => {
    const filtered = getFilteredData();
    const activeChannels = new Set();
    const allChannels = new Set();
    
    filtered.forEach(item => {
      allChannels.add(item.channel_id);
      if (item.implementation_percentage >= 50) {
        activeChannels.add(item.channel_id);
      }
    });
    
    return [
      { name: 'Implementados', value: activeChannels.size },
      { name: 'No Implementados', value: allChannels.size - activeChannels.size }
    ];
  };

  const COLORS = ['#4CAF50', '#f44336'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasFilters = selectedCountry !== 'all' || selectedCampaign !== 'all';
  const implementedChannels = calculateImplementedChannels();
  const countryData = calculateImplementedCountries();
  const countriesPieData = prepareCountriesPieData();
  const channelsPieData = prepareChannelsPieData();

  return (
    <Box>
      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>
        Analytics
      </Typography>

      {/* Filtros */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por País</InputLabel>
            <Select
              value={selectedCountry}
              label="Filtrar por País"
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <MenuItem value="all">Todos los Países</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por ToolBox</InputLabel>
            <Select
              value={selectedCampaign}
              label="Filtrar por ToolBox"
              onChange={(e) => setSelectedCampaign(e.target.value)}
            >
              <MenuItem value="all">Todas las ToolBox</MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {!hasFilters ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
            Selecciona un país o ToolBox para ver analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Usa los filtros de arriba para comenzar
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Métricas principales */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} sm={6} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <MuiTooltip
                    title="Canales únicos con implementation_percentage ≥ 50 dentro del filtro activo."
                    arrow
                    placement="top"
                  >
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help', mb: 0.5 }}>
                      <Typography color="textSecondary" variant={isMobile ? 'body2' : 'body1'}>
                        Canales Implementados
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </MuiTooltip>
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="div" color="primary">
                    {implementedChannels}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Canales con % &gt; 0
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <MuiTooltip
                    title="Un país se considera implementado cuando al menos el 50% de sus canales válidos tienen implementation_percentage ≥ 50. Solo aplica cuando hay una ToolBox seleccionada."
                    arrow
                    placement="top"
                  >
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help', mb: 0.5 }}>
                      <Typography color="textSecondary" variant={isMobile ? 'body2' : 'body1'}>
                        Países Implementados
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Box>
                  </MuiTooltip>
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="div" color="success.main">
                    {countryData.implemented} / {countryData.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Países con ≥50% canales activos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficos tipo Pie */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                  Implementación por Países
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={countriesPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={isMobile ? 80 : 100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {countriesPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                  Implementación por Canales
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={channelsPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={isMobile ? 80 : 100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {channelsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Analytics;

